import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

/* ===== SERVICES ===== */
import {
  createCampaign,
  updateCampaign,
  getCampaignById,
  assignUserToCampaign,
} from "../../services/campaignService";
import { getTMUsers, getSalesUsers } from "../../services/teamService";
import { getLeads } from "../../services/leadService";
import { addCampaignLeads } from "../../services/campaignLeadService";
import { getSalesUsersByTM } from "../../services/teamService";
import "./AddCampaignLayer.css";

const AddCampaignLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  /* ================= FORM STATE ================= */
  const [form, setForm] = useState({
    name: "",
    description: "",
    status: "active",
    start_date: "",
    end_date: "",
    call_start_time: "10:00",
    call_end_time: "18:30",
    campaign_type: "auto",
    priority: "high",
    max_attempts: 3,
    retry_after_minutes: 30,
    call_gap_seconds: 10,
    retry_on_status: "missed,busy,no-answer",
    close_on_status: "answered,not-interested",
    record_calls: 1,
    auto_pause_on_fail: 1,
    allow_manual_call: 1,
    notes: "",
  });

  /* ================= DATA ================= */
  const [teamManagers, setTeamManagers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [newLeads, setNewLeads] = useState([]);

  /* ================= SELECTION ================= */
  const [selectedTM, setSelectedTM] = useState([]);
  const [selectedSales, setSelectedSales] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const [loading, setLoading] = useState(false);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadUsers();
    loadNewLeads();

    if (isEdit) loadCampaign();
    else {
      const today = new Date().toISOString().split("T")[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setForm((p) => ({
        ...p,
        start_date: today,
        end_date: nextMonth.toISOString().split("T")[0],
      }));
    }
  }, [id]);

  const loadUsers = async () => {
    const tmRes = await getTMUsers();
    if (tmRes.status === "success") {
      setTeamManagers(tmRes.data || []);
    }
  };

  const loadNewLeads = async () => {
    const res = await getLeads();
    if (res.status === "success") {
      setNewLeads(
        (res.data || []).filter((l) => l.status?.toLowerCase() === "new")
      );
    }
  };

  const loadCampaign = async () => {
    const res = await getCampaignById(id);
    if (res.status === "success") {
      const c = res.data;
      setForm({
        ...c,
        call_start_time: c.call_start_time?.slice(0, 5),
        call_end_time: c.call_end_time?.slice(0, 5),
      });
    }
  };

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    });
  };

  const toggleSelect = (list, setList, id) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = (list, setList, items) => {
    if (list.length === items.length) {
      setList([]);
    } else {
      setList(items.map((item) => item.id));
    }
  };

  /* 🔥 UPDATED: MULTI TM SELECTION */
  const handleTMSelect = async (tmId) => {
    let updatedTM;

    if (selectedTM.includes(tmId)) {
      updatedTM = selectedTM.filter((id) => id !== tmId);
    } else {
      updatedTM = [...selectedTM, tmId];
    }

    setSelectedTM(updatedTM);
    
    // Reset selected sales when TM changes
    setSelectedSales([]);

    if (updatedTM.length === 0) {
      setSalesUsers([]);
      return;
    }

    let allSales = [];

    // Fetch sales users for each selected TM
    for (let id of updatedTM) {
      const res = await getSalesUsersByTM(id);
      if (res.status === "success") {
        allSales = [...allSales, ...(res.data.users || [])];
      }
    }

    /* 🔥 REMOVE DUPLICATES */
    const uniqueSales = Object.values(
      allSales.reduce((acc, s) => {
        acc[s.id] = s;
        return acc;
      }, {})
    );

    setSalesUsers(uniqueSales);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let campaignId;

      if (isEdit) {
        await updateCampaign(id, form);
        campaignId = id;
      } else {
        const res = await createCampaign(form);
        campaignId = res.data?.id || res.data;

        // Add leads to campaign
        if (selectedLeads.length > 0) {
          await addCampaignLeads({
            campaign_id: campaignId,
            lead_ids: selectedLeads,
          });
        }

        // Assign TMs to campaign
        if (selectedTM.length > 0) {
          // Updated to handle multiple TM assignment
          await assignUserToCampaign({
            campaign_id: campaignId,
            user_ids: selectedTM,
            roles: selectedTM.map(() => "tm"),
          });
        }

        // Assign Sales to campaign
        if (selectedSales.length > 0) {
          // Updated to handle multiple Sales assignment
          await assignUserToCampaign({
            campaign_id: campaignId,
            user_ids: selectedSales,
            roles: selectedSales.map(() => "sales"),
          });
        }
      }

      toast.success(isEdit ? "Campaign Updated" : "Campaign Created");
      navigate("/campaign-list");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="campaign-wrapper">
      {/* <div className="campaign-header">
        <h5>{isEdit ? "Edit Campaign" : "Create Campaign"}</h5>
        <button
          className="btn-secondary"
          onClick={() => navigate("/campaign-list")}
        >
          Back
        </button>
      </div> */}

      <form onSubmit={handleSubmit}>
        {/* ===== CAMPAIGN DETAILS ===== */}
        <div className="card">
              <div className="campaign-header">
        <h5>{isEdit ? "Edit Campaign" : "Create Campaign"}</h5>
        <button
          className="btn-secondary"
          onClick={() => navigate("/campaign-list")}
        >
          Back
        </button>
      </div>
          <h6>Campaign Details</h6>
          <div className="grid">
            <div className="form-group">
              <label>Campaign Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Call Start Time</label>
              <input
                type="time"
                name="call_start_time"
                value={form.call_start_time}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Call End Time</label>
              <input
                type="time"
                name="call_end_time"
                value={form.call_end_time}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* ===== CALL SETTINGS ===== */}
        <div className="card">
          <h6>Call Settings</h6>
          <div className="grid">
            <div className="form-group">
              <label>Max Attempts</label>
              <input
                type="number"
                name="max_attempts"
                value={form.max_attempts}
                onChange={handleChange}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Retry After (Minutes)</label>
              <input
                type="number"
                name="retry_after_minutes"
                value={form.retry_after_minutes}
                onChange={handleChange}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Call Gap (Seconds)</label>
              <input
                type="number"
                name="call_gap_seconds"
                value={form.call_gap_seconds}
                onChange={handleChange}
                min="1"
              />
            </div>
          </div>
        </div>

        {/* ===== OPTIONS ===== */}
        {/* <div className="card">
          <h6>Additional Options</h6>
          <div className="options-grid">
            <div className="option-item">
              <label>
                <input
                  type="checkbox"
                  name="record_calls"
                  checked={form.record_calls === 1}
                  onChange={handleChange}
                />
                Record Calls
              </label>
            </div>
            <div className="option-item">
              <label>
                <input
                  type="checkbox"
                  name="auto_pause_on_fail"
                  checked={form.auto_pause_on_fail === 1}
                  onChange={handleChange}
                />
                Auto Pause on Fail
              </label>
            </div>
            <div className="option-item">
              <label>
                <input
                  type="checkbox"
                  name="allow_manual_call"
                  checked={form.allow_manual_call === 1}
                  onChange={handleChange}
                />
                Allow Manual Call
              </label>
            </div>
          </div>
        </div> */}

        {/* ===== TEAM MANAGER ===== */}
        <div className="card">
          <div className="section-header">
            <h6>Team Managers ({selectedTM.length} selected)</h6>
            <button
              type="button"
              className="btn-select-all"
              onClick={() => selectAll(selectedTM, setSelectedTM, teamManagers)}
            >
              {selectedTM.length === teamManagers.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          <div className="selection-grid">
            {teamManagers.map((tm) => (
              <div
                key={tm.id}
                className={`selection-item ${
                  selectedTM.includes(tm.id) ? "selected" : ""
                }`}
                onClick={() => handleTMSelect(tm.id)}
              >
                <div className="selection-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTM.includes(tm.id)}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="selection-info">
                  <div className="selection-name">{tm.name}</div>
                  <div className="selection-meta">
                    {tm.email || "Team Manager"}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {teamManagers.length === 0 && (
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                textAlign: "center",
                padding: "20px 0",
              }}
            >
              No team managers available
            </p>
          )}
        </div>

        {/* ===== SALES ===== */}
        <div className="card">
          <div className="section-header">
            <h6>Sales Executives ({selectedSales.length} selected)</h6>
            <button
              type="button"
              className="btn-select-all"
              onClick={() => selectAll(selectedSales, setSelectedSales, salesUsers)}
            >
              {selectedSales.length === salesUsers.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          {selectedTM.length === 0 ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                textAlign: "center",
                padding: "20px 0",
              }}
            >
              Select at least one Team Manager to view Sales Executives
            </p>
          ) : (
            <>
              <div className="selection-grid">
                {salesUsers.map((s) => (
                  <div
                    key={s.id}
                    className={`selection-item ${
                      selectedSales.includes(s.id) ? "selected" : ""
                    }`}
                    onClick={() =>
                      toggleSelect(selectedSales, setSelectedSales, s.id)
                    }
                  >
                    <div className="selection-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSales.includes(s.id)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="selection-info">
                      <div className="selection-name">{s.name}</div>
                      <div className="selection-meta">
                        {s.email || "Sales Executive"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {salesUsers.length === 0 && (
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    textAlign: "center",
                    padding: "20px 0",
                  }}
                >
                  No sales executives available for selected Team Managers
                </p>
              )}
            </>
          )}
        </div>

        {/* ===== LEADS ===== */}
        <div className="card">
          <div className="section-header">
            <h6>New Leads ({selectedLeads.length} selected)</h6>
            <button
              type="button"
              className="btn-select-all"
              onClick={() => selectAll(selectedLeads, setSelectedLeads, newLeads)}
            >
              {selectedLeads.length === newLeads.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
          <div className="selection-grid">
            {newLeads.map((l) => (
              <div
                key={l.id}
                className={`selection-item ${
                  selectedLeads.includes(l.id) ? "selected" : ""
                }`}
                onClick={() =>
                  toggleSelect(selectedLeads, setSelectedLeads, l.id)
                }
              >
                <div className="selection-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(l.id)}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="selection-info">
                  <div className="selection-name">{l.name}</div>
                  <div className="selection-meta">{l.phone || "No phone"}</div>
                  {l.status && <span className="badge-new">{l.status}</span>}
                </div>
              </div>
            ))}
          </div>
          {newLeads.length === 0 && (
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                textAlign: "center",
                padding: "20px 0",
              }}
            >
              No new leads available
            </p>
          )}
        </div>

        {/* ===== NOTES ===== */}
        <div className="card">
          <h6>Additional Notes</h6>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Add any additional notes or instructions..."
            />
          </div>
        </div>

        <div className="action-buttons">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate("/campaign-list")}
          >
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={loading}>
            {loading
              ? "Saving..."
              : isEdit
              ? "Update Campaign"
              : "Create Campaign"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCampaignLayer;